import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

function genToken(): string {
  const alphabet = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
  let s = "";
  for (let i = 0; i < 10; i++) s += alphabet[Math.floor(Math.random() * alphabet.length)];
  return s;
}

/** Get or create the current user's personal referral token (guerrilla invite). */
export const getOrCreateViralInvite = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { source?: string; campaign?: string } | undefined) => ({
    source: input?.source ?? "whatsapp",
    campaign: input?.campaign ?? "guerrilla_quinielas",
  }))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: existing } = await supabase
      .from("viral_invites")
      .select("referral_token")
      .eq("referrer_user_id", userId)
      .eq("campaign", data.campaign)
      .limit(1)
      .maybeSingle();
    if (existing?.referral_token) return { token: existing.referral_token };

    // Single-flight token creation with retry on collision
    for (let i = 0; i < 4; i++) {
      const token = genToken();
      const { error } = await supabase.from("viral_invites").insert({
        referrer_user_id: userId,
        referral_token: token,
        source: data.source,
        campaign: data.campaign,
      });
      if (!error) return { token };
    }
    return { token: "", error: "Could not generate token" };
  });

/** Record a visit (anonymous, called when ?ref= is seen). */
export const recordReferralVisit = createServerFn({ method: "POST" })
  .inputValidator((input) =>
    z.object({
      token: z.string().min(6).max(32).regex(/^[A-Z0-9]+$/),
      session_id: z.string().min(1).max(64),
    }).parse(input),
  )
  .handler(async ({ data }) => {
    const { data: invite } = await supabaseAdmin
      .from("viral_invites")
      .select("referrer_user_id")
      .eq("referral_token", data.token)
      .maybeSingle();
    if (!invite) return { ok: false };
    await supabaseAdmin
      .from("viral_conversions")
      .upsert(
        {
          referral_token: data.token,
          referrer_user_id: invite.referrer_user_id,
          invited_session_id: data.session_id,
          status: "visited",
        },
        { onConflict: "referral_token,invited_session_id", ignoreDuplicates: true },
      );
    return { ok: true };
  });

/** Mark conversion as registered (called from authenticated context after sign-up). */
export const claimReferralRegistration = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({
      token: z.string().min(6).max(32).regex(/^[A-Z0-9]+$/),
      session_id: z.string().min(1).max(64),
    }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { userId } = context;
    const { data: invite } = await supabaseAdmin
      .from("viral_invites")
      .select("referrer_user_id")
      .eq("referral_token", data.token)
      .maybeSingle();
    if (!invite || invite.referrer_user_id === userId) return { ok: false };
    await supabaseAdmin
      .from("viral_conversions")
      .upsert(
        {
          referral_token: data.token,
          referrer_user_id: invite.referrer_user_id,
          invited_session_id: data.session_id,
          invited_user_id: userId,
          status: "registered",
          registered_at: new Date().toISOString(),
        },
        { onConflict: "referral_token,invited_session_id" },
      );
    return { ok: true };
  });

/** Personal stats for the current user (how many friends invited / registered). */
export const getMyViralStats = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data } = await supabase
      .from("viral_conversions")
      .select("status")
      .eq("referrer_user_id", userId);
    const rows = data ?? [];
    return {
      visited: rows.length,
      registered: rows.filter((r) => r.status === "registered").length,
    };
  });

/** Public viral leaderboard (top inviters). */
export const getViralLeaderboard = createServerFn({ method: "GET" })
  .inputValidator((input: { limit?: number } | undefined) => ({
    limit: Math.min(Math.max(input?.limit ?? 25, 1), 100),
  }))
  .handler(async ({ data }) => {
    const { data: rows } = await supabaseAdmin
      .from("viral_leaderboard")
      .select("user_id, registered_count, visited_count")
      .order("registered_count", { ascending: false })
      .limit(data.limit);
    return { rows: rows ?? [] };
  });